import { useState, useEffect, useRef } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useFormContext } from "react-hook-form";
import { useDebounced } from "../../../../hooks/useDebounce";
import {
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  TIKTOK,
} from "../../../../utils/constants";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetEstimationAudienceAdsCampaign } from "../../../../actions/adsCampaigns";
import { numberCompactFormat } from "../../../../utils/numbers";
import {
  AUDIENCE_SIZE,
  AUDIENCE_ESTIMATES,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import CardContainer from "../../../Card/CardContainer";
import differenceInDays from "date-fns/differenceInDays";

const AudienceEstimatesPreviewTikTok = ({ BASIC_PATH }) => {
  const {
    watch,
    getValues,
    audiencePreviewsCache,
    onChangeAudiencePreviewsCache,
  } = useFormContext();
  const abortControllerRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [estimationData, setEstimationData] = useState({
    audienceSize: "",
    potentialReach: "",
    estimatedDailyResults: "",
    cpm: "",
  });
  const [errorGenerateEstimation, setErrorGenerateEstimation] = useState(null);

  // Watch form data
  const budget = watch(`${BASIC_PATH}.budget`);
  const locations = watch(`${BASIC_PATH}.locations`);
  const genders = watch(`${BASIC_PATH}.genders`);
  const ageRange = watch(`${BASIC_PATH}.ageRange`);
  const interests = watch(`${BASIC_PATH}.interests`);
  const behaviors = watch(`${BASIC_PATH}.behaviors`);
  const customAudiences = watch(`${BASIC_PATH}.customAudiences`);
  const excludedCustomAudiences = watch(
    `${BASIC_PATH}.excludedCustomAudiences`
  );
  const languages = watch(`${BASIC_PATH}.languages`);
  const trackingID = watch(`${BASIC_PATH}.trackingID`);
  const trackingCustomEventID = watch(`${BASIC_PATH}.trackingCustomEventID`);
  const destinationType = watch(`${BASIC_PATH}.destinationType`);
  const typeBudget = watch(`${BASIC_PATH}.typeBudget`);
  const startDate = watch(`${BASIC_PATH}.startDate`);
  const endDate = watch(`${BASIC_PATH}.endDate`);

  const platformData = getValues(`platformsData.${TIKTOK}`);
  const specialAdCategories = platformData?.specialAdCategories || [];
  const integrationID = platformData?.integrationID;
  const adAccountID = platformData?.adAccountID;
  const objective = platformData?.objective;
  const isIOS14Campaign = platformData?.isIOS14Campaign;
  const campaignType = platformData?.type;
  const useCampaignBudget = platformData?.useCampaignBudget;
  const appID = isIOS14Campaign
    ? platformData?.appID
    : watch(`${BASIC_PATH}.appID`);

  const cachedData = audiencePreviewsCache?.[BASIC_PATH] || null;

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  // API call function
  const onGetAudienceEstimation = async (type) => {
    onChangeAudiencePreviewsCache({
      ...audiencePreviewsCache,
      [BASIC_PATH]: null,
    });

    if (type === "first") {
      if (cachedData) {
        setEstimationData(cachedData);
        setIsLoading(false);
        setIsFirstLoading(false);
        return;
      }
      setIsFirstLoading(false);
    }

    // Abort previous request if exists
    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);

    let finalBudget;

    if (useCampaignBudget) {
      finalBudget = 0;
    } else {
      if (typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION) {
        finalBudget = budget;
      } else {
        if (startDate && endDate) {
          const days = differenceInDays(endDate, startDate);
          finalBudget = Math.round(budget / days);
        } else {
          finalBudget = 0;
        }
      }
    }

    const response = await dispatch(
      startGetEstimationAudienceAdsCampaign(
        {
          platform: TIKTOK,
          integrationID,
          adAccountID,
          objective,
          campaignType,
          budget: finalBudget,
          locations,
          genders,
          ageRange,
          interests,
          behaviors,
          customAudiences,
          excludedCustomAudiences,
          languages,
          appID,
          isIOS14Campaign,
          specialAdCategories,
          destinationType,
        },
        abortControllerRef.current.signal
      )
    );

    if (!response.ok) {
      if (response.aborted) {
        setIsLoading(false);
        return;
      }
      setErrorGenerateEstimation(response.message);
    }

    if (response.ok) {
      const data = response.data;

      // Generate estimation data
      const newEstimationData = data;

      setEstimationData(newEstimationData);
      setErrorGenerateEstimation(null);

      onChangeAudiencePreviewsCache({
        ...audiencePreviewsCache,
        [BASIC_PATH]: newEstimationData,
      });
    }

    setIsLoading(false);
  };

  // Debounced function
  const debouncedEstimationRequest = useDebounced(
    onGetAudienceEstimation,
    3000
  );

  // Effect to trigger estimation when data changes
  useEffect(() => {
    setErrorGenerateEstimation(null);

    // Abort previous debounced request if exists
    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }

    debouncedEstimationRequest(isFirstLoading ? "first" : "change");

    // eslint-disable-next-line
  }, [
    budget,
    locations,
    genders,
    ageRange,
    interests,
    behaviors,
    customAudiences,
    excludedCustomAudiences,
    languages,
    trackingID,
    trackingCustomEventID,
    destinationType,
    appID,
  ]);

  // Cleanup effect to abort requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef?.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <CardContainer
      sx={{
        maxHeight: "calc(100vh - 60px)",
      }}
      sxContainer={{
        boxShadow: "none !important",
        mt: 0.5,
        mb: 0.5,
      }}
      title={
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t(AUDIENCE_ESTIMATES)}
        </Typography>
      }
    >
      {errorGenerateEstimation && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {errorGenerateEstimation}
        </Typography>
      )}

      {/* Metrics Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
        }}
      >
        {/* Audience Size */}
        <Box>
          <Typography variant="body2">{t(AUDIENCE_SIZE)}</Typography>
          {isLoading ? (
            <Skeleton variant="text" width="60%" height={24} />
          ) : (
            <Typography variant="body1">
              {`${numberCompactFormat(estimationData.minSize || 0)} - ${numberCompactFormat(estimationData.maxSize || 0)}`}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContainer>
  );
};

export default AudienceEstimatesPreviewTikTok;
